import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { toast } from "react-hot-toast";
import { baseAxios } from "../../../utils/config";
import { colors } from "../..";
import { PerformanceTab } from "./PerformanceTab";
import { ProductUsageTab } from "./ProductUsageTab";
import { StylistInsightsTab } from "./StylistInsightsTab";

const SUMMARY_CARDS = [
  { key: "totalFormulas", label: "Total Formulas Created", format: "int" },
  { key: "uniqueRecipes", label: "Unique Recipe Templates", format: "int" },
  {
    key: "avgProductsPerFormula",
    label: "Avg Products per Formula",
    format: "decimal1",
  },
  { key: "repeatRate", label: "Client Repeat Rate", format: "percent1" },
];

function formatSummaryValue(value, format) {
  const n = Number(value);
  if (Number.isNaN(n) || value == null) {
    if (format === "percent1") return "0.0%";
    if (format === "decimal1") return "0.0";
    return "0";
  }
  if (format === "percent1") return `${n.toFixed(1)}%`;
  if (format === "decimal1") return n.toFixed(1);
  return String(Math.round(n));
}

/**
 * Analytics View — summary metrics + performance / product / stylist tabs
 */
export function AnalyticsView({
  salon,
  startDate,
  endDate,
  validateDateRange,
  formatDateToISO,
  user,
  needsLocation,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasRun, setHasRun] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (needsLocation || !salon) {
      toast.error("Please select a location", { duration: 3000 });
      return;
    }
    if (startDate && endDate && !validateDateRange(startDate, endDate)) {
      return;
    }

    setLoadingSummary(true);
    setHasRun(true);
    try {
      const params = {
        locationId: salon?.location_id || user.location_id,
      };
      if (startDate) params.startDate = formatDateToISO(startDate);
      if (endDate) params.endDate = formatDateToISO(endDate);

      const response = await baseAxios.get(
        "/formula-tracker/analytics/summary",
        { params }
      );
      setSummary(response.data || null);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      setSummary(null);
      toast.error("Failed to load summary metrics", { duration: 3000 });
    } finally {
      setLoadingSummary(false);
    }
  }, [
    endDate,
    formatDateToISO,
    needsLocation,
    salon,
    startDate,
    user.location_id,
    validateDateRange,
  ]);

  // Auto-load when location/dates change
  useEffect(() => {
    if (needsLocation || !salon) {
      setSummary(null);
      return;
    }
    fetchSummary();
  }, [fetchSummary, needsLocation, salon]);

  const tabProps = {
    salon,
    startDate,
    endDate,
    formatDateToISO,
    user,
    needsLocation,
    refreshKey,
    hasRun,
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={fetchSummary}
          disabled={loadingSummary || needsLocation}
          sx={{
            minHeight: 40,
            px: 2,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {loadingSummary ? "Loading…" : "Load Analytics"}
        </Button>
      </Box>

      {summary && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {SUMMARY_CARDS.map(({ key, label, format }) => (
            <Box
              key={key}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "6px",
                bgcolor: "background.paper",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 0.75,
                  bgcolor: "secondary.main",
                  borderBottom: "1px solid",
                  borderColor: "primary.main",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.gold,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                  }}
                >
                  {label}
                </Typography>
              </Box>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography
                  fontWeight={700}
                  sx={{ fontSize: "1.75rem", lineHeight: 1.1 }}
                >
                  {formatSummaryValue(summary[key], format)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 2.5,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 40,
            },
            "& .Mui-selected": { color: "primary.main" },
            "& .MuiTabs-indicator": { bgcolor: "primary.main" },
          }}
        >
          <Tab label="Formula Performance" />
          <Tab label="Product Usage" />
          <Tab label="Stylist Insights" />
        </Tabs>
      </Box>

      <Box sx={{ display: activeTab === 0 ? "block" : "none" }}>
        <PerformanceTab {...tabProps} />
      </Box>
      <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
        <ProductUsageTab {...tabProps} />
      </Box>
      <Box sx={{ display: activeTab === 2 ? "block" : "none" }}>
        <StylistInsightsTab {...tabProps} />
      </Box>
    </Box>
  );
}
